import { useState, useEffect, useCallback, useRef } from 'react';
import JsBarcode from 'jsbarcode';
import {
  sendRewardsOtp,
  verifyRewardsOtp,
  fetchRewardsMe,
  logoutRewards,
  redeemReward,
  saveRewardsBirthday,
  clearRewardsClientState,
} from '../../api/rewardsClient';
import './rewards.css';

const REWARDS_TIERS = [
  {
    id: 'silver',
    name: 'Silver',
    minYearlySpend: 0,
    maxYearlySpend: 4999.99,
    pointsPerDollar: 1,
    perks: [],
    promo:
      'Spend under $5,000 in a calendar year for Silver and earn 1 point per $1. Silver is the starting tier for all rewards members.',
  },
  {
    id: 'gold',
    name: 'Gold',
    minYearlySpend: 5000,
    maxYearlySpend: 9999.99,
    pointsPerDollar: 1.5,
    perks: ['50% off alteration fees'],
    promo:
      'Spend $5,000–$9,999.99 in a calendar year to reach Gold. Earn 1.5 points per $1 on eligible purchases, plus 50% off alteration fees.',
  },
  {
    id: 'diamond',
    name: 'Diamond',
    minYearlySpend: 10000,
    maxYearlySpend: null,
    pointsPerDollar: 2,
    perks: ['Free shipping', 'Free alterations'],
    promo:
      'Spend $10,000+ in a calendar year to unlock Diamond. Earn 2 points per $1 — our best rewards rate — plus free shipping and free alterations.',
  },
];

/** Display copy only — the server owns the authoritative point costs. */
const REWARDS_REDEEM_OPTIONS = Array.from({ length: 20 }, (_, i) => {
  const amount = (i + 1) * 10;
  return {
    id: `off-${amount}`,
    label: `$${amount} off`,
    description: 'Discount off all items',
    type: 'fixed',
    amountOff: amount,
    pointsCost: (amount / 10) * 300,
  };
});

function formatPhoneDisplay(phone) {
  if (!phone || phone.length !== 10) return phone || '';
  return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`;
}

/** Normalize to 10-digit US (matches backend). Accepts with or without leading country code 1. */
function normalizeUsPhone10(raw) {
  if (raw == null) return '';
  let digits = String(raw).replace(/\D/g, '');
  while (digits.length > 10 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  return digits.length === 10 ? digits : '';
}

/** NANP: area code and exchange cannot start with 0 or 1. */
const US_PHONE_REGEX = /^[2-9]\d{2}[2-9]\d{6}$/;

function isValidUsPhone(raw) {
  const phone10 = normalizeUsPhone10(raw);
  return US_PHONE_REGEX.test(phone10);
}

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
}

function formatOrderDate(timestamp) {
  if (!timestamp) return 'Date unknown';
  return new Date(timestamp).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatStatusLabel(status) {
  const s = String(status || '').trim();
  if (!s) return '';
  return s
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function getStatusClassName(status) {
  const slug = (status || 'order created').toLowerCase().trim().replace(/\s+/g, '-');
  return `rewards-order-status rewards-order-status-${slug}`;
}

function TierInfoButton({ promo, perks = [] }) {
  return (
    <span className="rewards-tier-info-wrap">
      <button type="button" className="rewards-tier-info-btn" aria-label="Tier details">
        i
      </button>
      <span className="rewards-tier-tooltip" role="tooltip">
        <span className="rewards-tier-tooltip-promo">{promo}</span>
        {perks.length > 0 ? (
          <span className="rewards-tier-tooltip-benefits">
            <span className="rewards-tier-tooltip-benefits-title">Benefits</span>
            <ul>
              {perks.map((perk) => (
                <li key={perk}>{perk}</li>
              ))}
            </ul>
          </span>
        ) : (
          <span className="rewards-tier-tooltip-benefits">
            <span className="rewards-tier-tooltip-benefits-title">Benefits</span>
            <ul>
              <li>Earn points on eligible purchases</li>
            </ul>
          </span>
        )}
      </span>
    </span>
  );
}

function CustomerPhoneBarcode({ phone }) {
  const svgRef = useRef(null);
  const phone10 = normalizeUsPhone10(phone);

  useEffect(() => {
    if (!svgRef.current || !phone10) return;
    try {
      JsBarcode(svgRef.current, phone10, {
        format: 'CODE128',
        displayValue: true,
        fontSize: 14,
        height: 70,
        margin: 8,
        background: '#ffffff',
        lineColor: '#0f172a',
      });
    } catch (err) {
      console.error('Failed to render customer barcode:', err);
    }
  }, [phone10]);

  if (!phone10) {
    return <p className="rewards-barcode-error">Phone number unavailable for barcode.</p>;
  }

  return (
    <div className="rewards-barcode-wrap">
      <p className="rewards-barcode-hint">Show this barcode at the counter so staff can open your account.</p>
      <svg ref={svgRef} className="rewards-barcode-svg" role="img" aria-label={`Customer barcode ${phone10}`} />
    </div>
  );
}

function PreviousOrdersContent({ orders, highlightActive = false, activeOrders = [] }) {
  if (!orders?.length) {
    return <p className="rewards-empty">No orders on file yet.</p>;
  }

  const activeSet = new Set(
    (activeOrders || []).map((o) => String(o.orderNumber)).filter(Boolean)
  );
  const list = highlightActive
    ? [...orders].sort((a, b) => {
        const aActive = activeSet.has(String(a.orderNumber)) ? 1 : 0;
        const bActive = activeSet.has(String(b.orderNumber)) ? 1 : 0;
        return bActive - aActive;
      })
    : orders;

  return (
    <ul className="rewards-orders-list">
      {list.map((order) => {
        const isActive = highlightActive && activeSet.has(String(order.orderNumber));
        return (
          <li
            key={order.orderNumber}
            className={`rewards-order-card${isActive ? ' is-active' : ''}`}
          >
            <div className="rewards-order-number">Order {order.orderNumber}</div>
            <div className="rewards-order-date">{formatOrderDate(order.timestamp)}</div>
            {order.lineItems?.length > 0 && (
              <ul className="rewards-order-items">
                {order.lineItems.slice(0, 3).map((item, idx) => (
                  <li key={idx}>
                    {item.name}
                    {item.quantity > 1 ? ` × ${item.quantity}` : ''}
                  </li>
                ))}
                {order.lineItems.length > 3 && (
                  <li className="rewards-order-more">+{order.lineItems.length - 3} more items</li>
                )}
              </ul>
            )}
            {highlightActive && order.status ? (
              <span className={getStatusClassName(order.status)}>
                {formatStatusLabel(order.status)}
              </span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

function RewardsTiersContent({ profile }) {
  return (
    <>
      <p className="rewards-tiers-intro">
        Tiers are based on calendar-year spending. Higher tiers earn more points per $1 spent.
      </p>
      <div className="rewards-tier-cards">
        {REWARDS_TIERS.map((tier) => {
          const isCurrent = tier.id === profile?.tier?.id;
          const spendLabel =
            tier.maxYearlySpend != null
              ? `${formatCurrency(tier.minYearlySpend)} – ${formatCurrency(tier.maxYearlySpend)}`
              : `${formatCurrency(tier.minYearlySpend)}+`;

          return (
            <div
              key={tier.id}
              className={`rewards-tier-card rewards-tier-card-${tier.id} ${isCurrent ? 'is-current' : ''}`}
            >
              <div className="rewards-tier-card-header">
                <h3>{tier.name}</h3>
                <TierInfoButton promo={tier.promo} perks={tier.perks} />
              </div>
              <p className="rewards-tier-spend">{spendLabel} yearly spend</p>
              <p className="rewards-tier-rate">{tier.pointsPerDollar} pts / $1</p>
              {tier.perks?.length > 0 && (
                <ul className="rewards-tier-perks">
                  {tier.perks.map((perk) => (
                    <li key={perk}>{perk}</li>
                  ))}
                </ul>
              )}
              {isCurrent && <span className="rewards-tier-current-tag">Your tier</span>}
            </div>
          );
        })}
      </div>
    </>
  );
}

function MobileSheet({ title, onClose, children }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <div className="rewards-mobile-sheet" role="dialog" aria-modal="true" aria-label={title}>
      <button type="button" className="rewards-mobile-sheet-backdrop" onClick={onClose} aria-label="Close" />
      <div className="rewards-mobile-sheet-panel">
        <header className="rewards-mobile-sheet-header">
          <button type="button" className="rewards-mobile-sheet-back" onClick={onClose}>
            ← Back
          </button>
          <h2>{title}</h2>
          <span className="rewards-mobile-sheet-header-spacer" aria-hidden="true" />
        </header>
        <div className="rewards-mobile-sheet-body">{children}</div>
      </div>
    </div>
  );
}

function DiscountCodeRow({ redemption }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(redemption.code);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <li className="rewards-code-card">
      <div className="rewards-code-info">
        <span className="rewards-code-label">{redemption.label}</span>
        <span className="rewards-code-value">{redemption.code}</span>
        {redemption.expiresAt ? (
          <span className="rewards-code-expiry">Expires {formatOrderDate(redemption.expiresAt)}</span>
        ) : null}
      </div>
      <button
        type="button"
        className={`rewards-code-copy-btn ${copied ? 'is-copied' : ''}`}
        onClick={handleCopy}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </li>
  );
}

function ActiveTierBenefitRow({ benefit }) {
  return (
    <li className="rewards-code-card rewards-tier-benefit-card">
      <div className="rewards-code-info">
        <span className="rewards-code-label">Active reward</span>
        <span className="rewards-code-value rewards-tier-benefit-title">{benefit.title}</span>
        <span className="rewards-code-expiry">{benefit.description}</span>
      </div>
      <span className="rewards-tier-benefit-badge">Auto</span>
    </li>
  );
}

const BIRTHDAY_MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

function BirthdayGiftIcon() {
  return (
    <svg className="rewards-birthday-gift" viewBox="0 0 72 72" fill="none" aria-hidden="true">
      <path d="M14 12v14M7 19h14" stroke="#E8B45A" strokeWidth="2.6" strokeLinecap="round" />
      <path d="M56 8v10M51 13h10" stroke="#E8B45A" strokeWidth="2.2" strokeLinecap="round" />
      <path d="M24 24c1-8 9-12 12-5 3-7 11-3 12 5 0 5-7 9-12 11-5-2-12-6-12-11Z" fill="#F3B5A4" />
      <rect x="16" y="32" width="40" height="28" rx="6" fill="#F6C9BA" />
      <rect x="14" y="28" width="44" height="11" rx="4" fill="#F8D5C8" />
      <rect x="33.5" y="28" width="5" height="32" rx="1.5" fill="#E89B86" />
      <circle cx="36" cy="26" r="4.6" fill="#E08A74" />
    </svg>
  );
}

function BirthdaySavedChip({ label }) {
  return (
    <span className="rewards-birthday-saved">
      <button type="button" className="rewards-birthday-saved-date" aria-describedby="birthday-discount-tip">
        <BirthdayGiftIcon />
        {label}
      </button>
      <span id="birthday-discount-tip" className="rewards-birthday-saved-tip" role="tooltip">
        Enjoy 5% off any purchase during your birthday week. Redeem it in Claim rewards. It cannot
        be combined with other discounts.
      </span>
    </span>
  );
}

function birthdayIsSaved(birthday) {
  if (!birthday) return false;
  if (birthday.saved) return true;
  const month = Number(birthday.month);
  const day = Number(birthday.day);
  return month >= 1 && month <= 12 && day >= 1 && day <= 31;
}

function BirthdayRewardRow({ birthday, saving, error, notice, month, day, onMonth, onDay, onSave }) {
  if (birthdayIsSaved(birthday)) return null;

  return (
    <section className="rewards-birthday-row" aria-label="Birthday reward">
      <span className="rewards-birthday-spark" aria-hidden="true" />
      <div className="rewards-birthday-icon">
        <BirthdayGiftIcon />
      </div>
      <div className="rewards-birthday-body">
        <h2 className="rewards-birthday-heading">Your birthday deserves a little extra.</h2>
        <p className="rewards-birthday-lead">Enjoy 5% off any purchase during your birthday week.</p>
        <p className="rewards-birthday-hint">
          Add your birthday and we&apos;ll text you when your reward is ready.
        </p>
        <form className="rewards-birthday-form" onSubmit={onSave}>
          <label className="rewards-birthday-field">
            <span>Month</span>
            <select value={month} onChange={(e) => onMonth(e.target.value)} required>
              <option value="">Month</option>
              {BIRTHDAY_MONTHS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <label className="rewards-birthday-field rewards-birthday-field-day">
            <span>Day</span>
            <select value={day} onChange={(e) => onDay(e.target.value)} required>
              <option value="">Day</option>
              {Array.from({ length: 31 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <button type="submit" className="rewards-birthday-save" disabled={saving || !month || !day}>
            {saving ? 'Saving…' : 'Save my birthday'}
          </button>
        </form>
        {notice ? <p className="rewards-birthday-ok">{notice}</p> : null}
        {error ? <p className="rewards-birthday-err">{error}</p> : null}
        <p className="rewards-birthday-fine">
          Redeem in Claim rewards. Cannot be combined with other discounts.
        </p>
      </div>
    </section>
  );
}

function ClaimRewardsContent({
  availablePoints,
  claimNotice,
  claimError,
  redeemingId,
  redemptions,
  activeTierBenefits,
  redeemOptions,
  birthday,
  onRedeem,
}) {
  const busy = Boolean(redeemingId);
  const hasActive =
    (activeTierBenefits && activeTierBenefits.length > 0) || (redemptions && redemptions.length > 0);
  const options =
    Array.isArray(redeemOptions) && redeemOptions.length > 0
      ? redeemOptions
      : REWARDS_REDEEM_OPTIONS;

  const percentOptions = options.filter(
    (o) => o.type === 'percentage' || o.percentageOff != null || String(o.id || '').startsWith('pct-')
  );
  const fixedOptions = options
    .filter(
      (o) =>
        o.type === 'fixed' ||
        (o.amountOff != null && o.percentageOff == null && o.type !== 'percentage') ||
        String(o.id || '').startsWith('off-')
    )
    .slice()
    .sort((a, b) => (Number(a.amountOff) || 0) - (Number(b.amountOff) || 0));

  const affordableFixed = fixedOptions.filter((o) => availablePoints >= o.pointsCost);
  const defaultFixedId =
    affordableFixed[affordableFixed.length - 1]?.id || fixedOptions[0]?.id || null;
  const [selectedFixedId, setSelectedFixedId] = useState(defaultFixedId);
  const fixedIdsKey = fixedOptions.map((o) => o.id).join('|');

  const affordablePercent = percentOptions.filter((o) => availablePoints >= o.pointsCost);
  const defaultPercentId =
    affordablePercent[affordablePercent.length - 1]?.id || percentOptions[0]?.id || null;
  const [selectedPercentId, setSelectedPercentId] = useState(defaultPercentId);
  const percentIdsKey = percentOptions.map((o) => o.id).join('|');

  useEffect(() => {
    if (!fixedIdsKey) return;
    const ids = fixedIdsKey.split('|');
    if (!selectedFixedId || !ids.includes(selectedFixedId)) {
      setSelectedFixedId(defaultFixedId);
    }
  }, [defaultFixedId, fixedIdsKey, selectedFixedId]);

  useEffect(() => {
    if (!percentIdsKey) return;
    const ids = percentIdsKey.split('|');
    if (!selectedPercentId || !ids.includes(selectedPercentId)) {
      setSelectedPercentId(defaultPercentId);
    }
  }, [defaultPercentId, percentIdsKey, selectedPercentId]);

  const selectedFixed =
    fixedOptions.find((o) => o.id === selectedFixedId) || fixedOptions[0] || null;
  const fixedCanAfford = selectedFixed ? availablePoints >= selectedFixed.pointsCost : false;
  const isRedeemingFixed = selectedFixed && redeemingId === selectedFixed.id;

  const selectedPercent =
    percentOptions.find((o) => o.id === selectedPercentId) || percentOptions[0] || null;
  const percentCanAfford = selectedPercent ? availablePoints >= selectedPercent.pointsCost : false;
  const isRedeemingPercent = selectedPercent && redeemingId === selectedPercent.id;

  return (
    <>
      <p className="rewards-claim-intro">
        You have <strong>{availablePoints.toLocaleString()}</strong> points. Pick a dollar discount.
      </p>

      {birthdayIsSaved(birthday) ? (
        <section className="rewards-claim-group rewards-claim-birthday" aria-label="Birthday discount">
          <div className="rewards-claim-group-header">
            <h3 className="rewards-claim-group-title">Birthday gift</h3>
            <span className="rewards-claim-group-hint">
              {birthday.canClaim
                ? 'This week only · not combined with other discounts'
                : birthday.claimedThisYear
                  ? 'Already claimed this year'
                  : 'Available the week of your birthday'}
            </span>
          </div>
          <div className="rewards-claim-fixed-summary rewards-claim-birthday-summary">
            <div className="rewards-claim-fixed-summary-copy">
              <span className="rewards-claim-fixed-summary-label">5% off any purchase</span>
              <span className="rewards-claim-fixed-summary-cost">
                {birthday.canClaim
                  ? 'Free · claim now'
                  : birthday.claimedThisYear
                    ? 'Claimed for this birthday week'
                    : birthday.label
                      ? `Opens the week of ${birthday.label}`
                      : 'Opens during your birthday week'}
              </span>
            </div>
            {birthday.canClaim ? (
              <button
                type="button"
                className="rewards-claim-fixed-claim-btn rewards-claim-birthday-btn"
                disabled={busy}
                onClick={() => onRedeem({ id: 'birthday-5', label: 'Birthday 5% off' })}
              >
                {redeemingId === 'birthday-5' ? 'Creating…' : 'Claim gift'}
              </button>
            ) : null}
          </div>
        </section>
      ) : null}

      {percentOptions.length > 0 && selectedPercent && (
        <section className="rewards-claim-group" aria-label="Percent off">
          <div className="rewards-claim-group-header">
            <h3 className="rewards-claim-group-title">Percent off</h3>
            <span className="rewards-claim-group-hint">Discount off all items</span>
          </div>
          <div className="rewards-claim-percent-grid" role="listbox" aria-label="Percent discount">
            {percentOptions.map((option) => {
              const canAfford = availablePoints >= option.pointsCost;
              const selected = option.id === selectedPercent.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`rewards-claim-percent-btn${selected ? ' is-selected' : ''}${
                    !canAfford ? ' is-locked' : ''
                  }`}
                  disabled={busy}
                  title={
                    canAfford
                      ? `${option.label} — ${option.pointsCost.toLocaleString()} pts`
                      : `Need ${option.pointsCost.toLocaleString()} points`
                  }
                  onClick={() => setSelectedPercentId(option.id)}
                >
                  <span className="rewards-claim-percent-label">
                    {option.label.replace(/\s*off$/i, '')}
                  </span>
                  <span className="rewards-claim-percent-cost">
                    {option.pointsCost.toLocaleString()} pts
                  </span>
                </button>
              );
            })}
          </div>
          <div className="rewards-claim-fixed-summary">
            <div className="rewards-claim-fixed-summary-copy">
              <span className="rewards-claim-fixed-summary-label">{selectedPercent.label}</span>
              <span className="rewards-claim-fixed-summary-cost">
                {selectedPercent.pointsCost.toLocaleString()} points
                {!percentCanAfford ? ' · not enough points' : ''}
              </span>
            </div>
            <button
              type="button"
              className="rewards-claim-fixed-claim-btn"
              disabled={!percentCanAfford || busy}
              onClick={() => onRedeem(selectedPercent)}
            >
              {isRedeemingPercent ? 'Creating…' : 'Claim'}
            </button>
          </div>
        </section>
      )}

      {fixedOptions.length > 0 && selectedFixed && (
        <section className="rewards-claim-group" aria-label="Dollar off">
          <div className="rewards-claim-group-header">
            <h3 className="rewards-claim-group-title">Dollar off</h3>
            <span className="rewards-claim-group-hint">Discount off all items</span>
          </div>
          <div className="rewards-claim-amount-chips" role="listbox" aria-label="Discount amount">
            {fixedOptions.map((option) => {
              const canAfford = availablePoints >= option.pointsCost;
              const selected = option.id === selectedFixed.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`rewards-claim-amount-chip${selected ? ' is-selected' : ''}${
                    !canAfford ? ' is-locked' : ''
                  }`}
                  disabled={busy}
                  title={
                    canAfford
                      ? `${option.label} — ${option.pointsCost.toLocaleString()} pts`
                      : `Need ${option.pointsCost.toLocaleString()} points`
                  }
                  onClick={() => setSelectedFixedId(option.id)}
                >
                  ${option.amountOff ?? option.label.replace(/\D/g, '')}
                </button>
              );
            })}
          </div>
          <div className="rewards-claim-fixed-summary">
            <div className="rewards-claim-fixed-summary-copy">
              <span className="rewards-claim-fixed-summary-label">{selectedFixed.label}</span>
              <span className="rewards-claim-fixed-summary-cost">
                {selectedFixed.pointsCost.toLocaleString()} points
                {!fixedCanAfford ? ' · not enough points' : ''}
              </span>
            </div>
            <button
              type="button"
              className="rewards-claim-fixed-claim-btn"
              disabled={!fixedCanAfford || busy}
              onClick={() => onRedeem(selectedFixed)}
            >
              {isRedeemingFixed ? 'Creating…' : 'Claim'}
            </button>
          </div>
        </section>
      )}

      {claimNotice ? <p className="rewards-claim-notice">{claimNotice}</p> : null}
      {claimError ? <p className="rewards-claim-error">{claimError}</p> : null}

      {hasActive && (
        <section className="rewards-codes-section">
          <h3 className="rewards-codes-title">Active rewards</h3>
          {activeTierBenefits?.length > 0 && (
            <>
              <p className="rewards-codes-hint">
                Tier benefits on your account — applied automatically at checkout when you&apos;re signed in.
              </p>
              <ul className="rewards-codes-list">
                {activeTierBenefits.map((benefit) => (
                  <ActiveTierBenefitRow key={benefit.id} benefit={benefit} />
                ))}
              </ul>
            </>
          )}
          {redemptions?.length > 0 && (
            <>
              <p className={`rewards-codes-hint ${activeTierBenefits?.length ? 'rewards-codes-hint-spaced' : ''}`}>
                Point codes — enter at checkout. Each works once and only on your account.
              </p>
              <ul className="rewards-codes-list">
                {redemptions.map((redemption) => (
                  <DiscountCodeRow key={redemption.id} redemption={redemption} />
                ))}
              </ul>
            </>
          )}
        </section>
      )}
    </>
  );
}

function Rewards() {
  const [step, setStep] = useState('phone');
  const [authReady, setAuthReady] = useState(false);
  const [phone, setPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSessionId, setOtpSessionId] = useState('');
  const [phoneMasked, setPhoneMasked] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [rewardsData, setRewardsData] = useState(null);
  const [claimPanelOpen, setClaimPanelOpen] = useState(false);
  const [claimNotice, setClaimNotice] = useState('');
  const [claimError, setClaimError] = useState('');
  const [redeemingId, setRedeemingId] = useState(null);
  const [barcodeVisible, setBarcodeVisible] = useState(false);
  const [mobileSheet, setMobileSheet] = useState(null);
  const [birthdayMonth, setBirthdayMonth] = useState('');
  const [birthdayDay, setBirthdayDay] = useState('');
  const [birthdaySaving, setBirthdaySaving] = useState(false);
  const [birthdayError, setBirthdayError] = useState('');
  const [birthdayNotice, setBirthdayNotice] = useState('');

  const phoneIsValid = isValidUsPhone(phone);
  const showPhoneValidation = phoneTouched && phone.trim().length > 0;

  const applyRewardsPayload = useCallback((data) => {
    setRewardsData({
      profile: data.profile,
      orders: data.orders || [],
      activeOrders: data.activeOrders || [],
      redemptions: data.redemptions || [],
      activeTierBenefits: data.activeTierBenefits || [],
      yearResetNote: data.yearResetNote || '',
    });
    setPhoneMasked(data.profile?.phone ? formatPhoneDisplay(data.profile.phone) : '');
    setStep('dashboard');
  }, []);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const fromQuery = normalizeUsPhone10(params.get('phone') || '');
      if (fromQuery) setPhone(fromQuery);
    } catch {
      // ignore malformed query
    }
  }, []);

  // Rewards-only viewport (Shopify embed / mobile): do not change the rest of the app.
  useEffect(() => {
    const content =
      'width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover';
    let meta = document.querySelector('meta[name="viewport"]');
    const created = !meta;
    const previous = meta?.getAttribute('content') || '';

    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'viewport');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);

    return () => {
      if (created) {
        meta.remove();
      } else if (meta) {
        meta.setAttribute('content', previous || 'width=device-width, initial-scale=1');
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchRewardsMe();
        if (!cancelled) applyRewardsPayload(data);
      } catch {
        if (!cancelled) setStep('phone');
      } finally {
        if (!cancelled) setAuthReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyRewardsPayload]);

  const handleSendOtp = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setPhoneTouched(true);
    if (!phoneIsValid) {
      setSearchError('Enter a valid phone number on your account (with or without the leading 1).');
      return;
    }

    setSendingOtp(true);
    setSearchError('');

    try {
      const phone10 = normalizeUsPhone10(phone);
      const data = await sendRewardsOtp(phone10);
      setOtpSessionId(data.otpSessionId || '');
      setPhoneMasked(data.phoneMasked || '');
      setOtpCode('');
      setStep('otp');
    } catch (err) {
      setSearchError(err.message || 'Unable to send verification code.');
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otpSessionId || otpCode.length !== 6) {
      setSearchError('Enter the 6-digit code from your text message.');
      return;
    }

    setVerifyingOtp(true);
    setSearchError('');

    try {
      const data = await verifyRewardsOtp({
        phone: normalizeUsPhone10(phone),
        otpSessionId,
        code: otpCode,
      });
      applyRewardsPayload(data);
    } catch (err) {
      setSearchError(err.message || 'Invalid or expired verification code.');
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleRedeem = async (option) => {
    if (redeemingId) return;
    setRedeemingId(option.id);
    setClaimNotice('');
    setClaimError('');

    try {
      const data = await redeemReward(option.id);
      setRewardsData((prev) =>
        prev
          ? {
              ...prev,
              profile: { ...prev.profile, points: data.points },
              redemptions: [data.redemption, ...(prev.redemptions || [])],
              birthday:
                option.id === 'birthday-5'
                  ? { ...prev.birthday, canClaim: false, claimedThisYear: true, weekActive: true }
                  : prev.birthday,
            }
          : prev
      );
      setClaimNotice(`${option.label} unlocked. Copy your code below and use it at checkout.`);
    } catch (err) {
      setClaimError(err.message || 'Unable to redeem this reward.');
    } finally {
      setRedeemingId(null);
    }
  };

  const handleSignOut = async () => {
    try {
      await logoutRewards();
    } catch (err) {
      console.error('Logout failed:', err);
    }
    clearRewardsClientState();
    setRewardsData(null);
    setOtpSessionId('');
    setOtpCode('');
    setPhoneMasked('');
    setSearchError('');
    setPhone('');
    setPhoneTouched(false);
    setClaimPanelOpen(false);
    setClaimNotice('');
    setClaimError('');
    setRedeemingId(null);
    setBarcodeVisible(false);
    setMobileSheet(null);
    setBirthdayMonth('');
    setBirthdayDay('');
    setBirthdaySaving(false);
    setBirthdayError('');
    setBirthdayNotice('');
    setStep('phone');
  };

  const handleSaveBirthday = async (event) => {
    event.preventDefault();
    if (birthdaySaving) return;
    setBirthdaySaving(true);
    setBirthdayError('');
    setBirthdayNotice('');
    try {
      const data = await saveRewardsBirthday({
        month: Number(birthdayMonth),
        day: Number(birthdayDay),
      });
      const nextBirthday = birthdayIsSaved(data?.birthday)
        ? data.birthday
        : {
            ...(data?.birthday || {}),
            saved: true,
            month: Number(birthdayMonth),
            day: Number(birthdayDay),
            label: data?.birthday?.label || '',
          };
      setRewardsData((prev) => (prev ? { ...prev, birthday: nextBirthday } : prev));
      setBirthdayMonth('');
      setBirthdayDay('');
      setBirthdayNotice('');
    } catch (err) {
      setBirthdayError(err.message || 'Unable to save your birthday.');
    } finally {
      setBirthdaySaving(false);
    }
  };

  const handleBackToPhone = () => {
    setOtpSessionId('');
    setOtpCode('');
    setPhoneMasked('');
    setSearchError('');
    setPhoneTouched(false);
    setStep('phone');
  };

  if (!authReady) {
    return (
      <div className="rewards-page">
        <p className="rewards-loading-page">Loading…</p>
      </div>
    );
  }

  const profile = rewardsData?.profile;
  const orders = rewardsData?.orders || [];
  const activeOrders = rewardsData?.activeOrders || [];
  const redemptions = rewardsData?.redemptions || [];
  const activeTierBenefits = rewardsData?.activeTierBenefits || [];
  const redeemOptions = rewardsData?.redeemOptions || REWARDS_REDEEM_OPTIONS;
  const birthday = rewardsData?.birthday || null;
  const showCurrentOrdersPanel = activeOrders.length > 0;
  const availablePoints = profile?.points || 0;

  return (
    <div className={`rewards-page ${step === 'dashboard' ? 'rewards-page-expanded' : ''}`}>
      <div
        className={`rewards-container ${step === 'dashboard' ? 'rewards-container-expanded' : ''} ${
          showCurrentOrdersPanel ? 'rewards-container-with-current' : ''
        } ${claimPanelOpen ? 'rewards-container-with-claim' : ''}`}
      >
        {step !== 'dashboard' ? (
          <>
            <header className="rewards-header">
              <div className="rewards-logo">J&Z</div>
              <h1>Rewards</h1>
            </header>
            <main className="rewards-main">
              {step === 'phone' ? (
                <form className="rewards-form" onSubmit={handleSendOtp} noValidate>
                  <label htmlFor="rewards-phone" className="rewards-label">
                    Phone number
                  </label>
                  <p className="rewards-phone-intro">
                    Sign in with the phone number on your J&amp;Z account.
                  </p>
                  <input
                    id="rewards-phone"
                    type="tel"
                    className={`rewards-phone-input ${
                      showPhoneValidation ? (phoneIsValid ? 'rewards-phone-input-valid' : 'rewards-phone-input-invalid') : ''
                    }`}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    onBlur={() => setPhoneTouched(true)}
                    autoComplete="tel"
                    inputMode="tel"
                    title="US phone number with or without country code 1"
                    aria-invalid={showPhoneValidation && !phoneIsValid}
                  />
                  <button
                    type="submit"
                    className="rewards-search-btn"
                    disabled={!phoneIsValid || sendingOtp}
                  >
                    {sendingOtp ? 'Sending code…' : 'Send verification code'}
                  </button>
                </form>
              ) : (
                <form className="rewards-form" onSubmit={handleVerifyOtp}>
                  <p className="rewards-otp-intro">
                    Enter the 6-digit code sent to <strong>{phoneMasked || 'your phone'}</strong>.
                  </p>
                  <label htmlFor="rewards-otp" className="rewards-label">
                    Verification code
                  </label>
                  <input
                    id="rewards-otp"
                    type="text"
                    className="rewards-otp-input"
                    placeholder="000000"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                  />
                  <button
                    type="submit"
                    className="rewards-search-btn"
                    disabled={otpCode.length !== 6 || verifyingOtp}
                  >
                    {verifyingOtp ? 'Verifying…' : 'Verify & continue'}
                  </button>
                  <div className="rewards-otp-actions">
                    <button type="button" className="rewards-link-btn" onClick={handleBackToPhone}>
                      Change number
                    </button>
                    <button
                      type="button"
                      className="rewards-link-btn"
                      onClick={handleSendOtp}
                      disabled={sendingOtp}
                    >
                      {sendingOtp ? 'Sending…' : 'Resend code'}
                    </button>
                  </div>
                </form>
              )}
              {searchError && <p className="rewards-error">{searchError}</p>}
            </main>
          </>
        ) : (
          <div className="rewards-dashboard-row">
            {showCurrentOrdersPanel && (
              <aside className="rewards-current-orders-panel">
                <h2>Current order{activeOrders.length !== 1 ? 's' : ''}</h2>
                <ul className="rewards-current-orders-list">
                  {activeOrders.map((order) => (
                    <li key={order.orderNumber} className="rewards-current-order-card">
                      <div className="rewards-order-number">Order {order.orderNumber}</div>
                      <div className="rewards-order-date">{formatOrderDate(order.timestamp)}</div>
                      {order.status ? (
                        <span className={getStatusClassName(order.status)}>
                          {formatStatusLabel(order.status)}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </aside>
            )}

            <div className="rewards-dashboard">
              <header className="rewards-profile-header">
                <div className="rewards-profile-top">
                  {birthdayIsSaved(birthday) ? <BirthdaySavedChip label={birthday.label} /> : null}
                  <button type="button" className="rewards-search-again-btn" onClick={handleSignOut}>
                    Sign out
                  </button>
                </div>
                <p className="rewards-profile-phone">
                  {formatPhoneDisplay(normalizeUsPhone10(profile?.phone) || profile?.phone) ||
                    phoneMasked}
                </p>
                <h1 className="rewards-customer-name">{profile?.name || 'Customer'}</h1>

                <p className="rewards-points">
                  <span className="rewards-points-value">{availablePoints.toLocaleString()}</span>
                  <span className="rewards-points-label">points</span>
                </p>

                <div className="rewards-progress-wrap">
                  <div className="rewards-progress-track" aria-hidden="true">
                    <div
                      className={`rewards-progress-fill rewards-progress-fill-${profile?.tier?.id}`}
                      style={{ width: `${profile?.tier?.progressPercent || 0}%` }}
                    />
                  </div>
                  {profile?.tier?.nextTierName ? (
                    <span className="rewards-progress-next">
                      {formatCurrency(profile.yearlySpend)} /{' '}
                      {formatCurrency(profile.tier.nextTierMinSpend)} to {profile.tier.nextTierName}
                    </span>
                  ) : (
                    <span className="rewards-progress-next">Top tier unlocked</span>
                  )}
                  <p className="rewards-year-reset-note">
                    {rewardsData?.yearResetNote ||
                      'Spending and tier status reset every January 1. Points do not expire and carry over year to year.'}
                  </p>
                  <div className="rewards-progress-labels">
                    <span className={`rewards-tier-badge rewards-tier-badge-${profile?.tier?.id}`}>
                      {profile?.tier?.name}
                    </span>
                  </div>
                </div>

                <div className="rewards-header-actions">
                  <button
                    type="button"
                    className={`rewards-claim-open-btn ${claimPanelOpen || mobileSheet === 'claim' ? 'is-open' : ''}`}
                    onClick={() => {
                      setClaimNotice('');
                      if (typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches) {
                        setMobileSheet((s) => (s === 'claim' ? null : 'claim'));
                        setClaimPanelOpen(false);
                      } else {
                        setClaimPanelOpen((open) => !open);
                        setMobileSheet(null);
                      }
                    }}
                    aria-expanded={claimPanelOpen || mobileSheet === 'claim'}
                  >
                    {claimPanelOpen || mobileSheet === 'claim' ? 'Close rewards' : 'Claim rewards'}
                  </button>
                  <button
                    type="button"
                    className={`rewards-barcode-toggle-btn ${
                      barcodeVisible || mobileSheet === 'barcode' ? 'is-open' : ''
                    }`}
                    onClick={() => {
                      if (typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches) {
                        setMobileSheet((s) => (s === 'barcode' ? null : 'barcode'));
                        setBarcodeVisible(false);
                      } else {
                        setBarcodeVisible((v) => !v);
                        setMobileSheet(null);
                      }
                    }}
                    aria-expanded={barcodeVisible || mobileSheet === 'barcode'}
                  >
                    {barcodeVisible || mobileSheet === 'barcode' ? 'Hide barcode' : 'Show barcode'}
                  </button>
                </div>
                {barcodeVisible && <CustomerPhoneBarcode phone={profile?.phone} />}
              </header>

              <nav className="rewards-mobile-nav" aria-label="Rewards sections">
                <button
                  type="button"
                  className="rewards-mobile-nav-btn"
                  onClick={() => setMobileSheet('orders')}
                >
                  <span className="rewards-mobile-nav-title">Orders</span>
                  <span className="rewards-mobile-nav-meta">
                    {activeOrders.length} active · {orders.length} total
                  </span>
                  <span className="rewards-mobile-nav-chevron" aria-hidden="true">
                    →
                  </span>
                </button>
                <button
                  type="button"
                  className="rewards-mobile-nav-btn"
                  onClick={() => setMobileSheet('tiers')}
                >
                  <span className="rewards-mobile-nav-title">Rewards tiers</span>
                  <span className="rewards-mobile-nav-meta">
                    {profile?.tier?.name || 'Silver'} status & benefits
                  </span>
                  <span className="rewards-mobile-nav-chevron" aria-hidden="true">
                    →
                  </span>
                </button>
                <button
                  type="button"
                  className="rewards-mobile-nav-btn"
                  onClick={() => {
                    setClaimNotice('');
                    setClaimError('');
                    setMobileSheet('claim');
                  }}
                >
                  <span className="rewards-mobile-nav-title">Claim rewards</span>
                  <span className="rewards-mobile-nav-meta">
                    {birthday?.canClaim
                      ? 'Birthday gift ready to claim'
                      : `${availablePoints.toLocaleString()} pts available · ${
                          redemptions.length === 0
                            ? 'No active discounts'
                            : `${redemptions.length} active discount${redemptions.length === 1 ? '' : 's'}`
                        }`}
                  </span>
                  <span className="rewards-mobile-nav-chevron" aria-hidden="true">
                    →
                  </span>
                </button>
                <button
                  type="button"
                  className="rewards-mobile-nav-btn"
                  onClick={() => setMobileSheet('barcode')}
                >
                  <span className="rewards-mobile-nav-title">Store barcode</span>
                  <span className="rewards-mobile-nav-meta">Show at the counter</span>
                  <span className="rewards-mobile-nav-chevron" aria-hidden="true">
                    →
                  </span>
                </button>
              </nav>

              <BirthdayRewardRow
                birthday={birthday}
                saving={birthdaySaving}
                error={birthdayError}
                notice={birthdayNotice}
                month={birthdayMonth}
                day={birthdayDay}
                onMonth={setBirthdayMonth}
                onDay={setBirthdayDay}
                onSave={handleSaveBirthday}
              />

              <div className="rewards-body-grid">
                <aside className="rewards-orders-panel">
                  <h2>Previous orders</h2>
                  <PreviousOrdersContent orders={orders} />
                </aside>

                <section className="rewards-tiers-panel">
                  <h2>Rewards tiers</h2>
                  <RewardsTiersContent profile={profile} />
                </section>
              </div>
            </div>

            {mobileSheet === 'orders' && (
              <MobileSheet title="Orders" onClose={() => setMobileSheet(null)}>
                <PreviousOrdersContent
                  orders={orders}
                  highlightActive
                  activeOrders={activeOrders}
                />
              </MobileSheet>
            )}
            {mobileSheet === 'tiers' && (
              <MobileSheet title="Rewards tiers" onClose={() => setMobileSheet(null)}>
                <RewardsTiersContent profile={profile} />
              </MobileSheet>
            )}
            {mobileSheet === 'barcode' && (
              <MobileSheet title="Store barcode" onClose={() => setMobileSheet(null)}>
                <div className="rewards-barcode-sheet">
                  <CustomerPhoneBarcode phone={profile?.phone} />
                </div>
              </MobileSheet>
            )}
            {mobileSheet === 'claim' && (
              <MobileSheet
                title="Claim rewards"
                onClose={() => {
                  setMobileSheet(null);
                  setClaimNotice('');
                  setClaimError('');
                }}
              >
                <ClaimRewardsContent
                  availablePoints={availablePoints}
                  claimNotice={claimNotice}
                  claimError={claimError}
                  redeemingId={redeemingId}
                  redemptions={redemptions}
                  activeTierBenefits={activeTierBenefits}
                  redeemOptions={redeemOptions}
                  birthday={birthday}
                  onRedeem={handleRedeem}
                />
              </MobileSheet>
            )}

            {claimPanelOpen && (
              <aside className="rewards-claim-panel rewards-claim-panel-desktop" aria-label="Claim rewards">
                <div className="rewards-claim-panel-header">
                  <h2>Claim rewards</h2>
                  <button
                    type="button"
                    className="rewards-claim-close-btn"
                    onClick={() => {
                      setClaimPanelOpen(false);
                      setClaimNotice('');
                      setClaimError('');
                    }}
                    aria-label="Close claim panel"
                  >
                    ×
                  </button>
                </div>
                <ClaimRewardsContent
                  availablePoints={availablePoints}
                  claimNotice={claimNotice}
                  claimError={claimError}
                  redeemingId={redeemingId}
                  redemptions={redemptions}
                  activeTierBenefits={activeTierBenefits}
                  redeemOptions={redeemOptions}
                  birthday={birthday}
                  onRedeem={handleRedeem}
                />
              </aside>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Rewards;
